import React, { useState, useEffect, useRef } from 'react';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, collection, query, where, getDocs, arrayUnion, arrayRemove, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import Modal from './Modal';
import RelationshipField from './RelationshipField';
import { toast } from 'react-hot-toast';
import * as faceapi from '@vladmandic/face-api';

const EditCardForm = ({ onClose, card, existingGroups = [], currentUserId, cards = [] }) => {
  // Add detailed logging
  console.log('EditCardForm - Full card data:', JSON.stringify(card, null, 2));
  console.log('EditCardForm - First name:', card?.firstName);
  console.log('EditCardForm - Last name:', card?.lastName);
  console.log('EditCardForm - Groups:', card?.groups);
  console.log('EditCardForm - Group:', card?.group);
  
  // Form Fields - Add null checks and default values
  const [firstName, setFirstName] = useState(card?.firstName || '');
  const [lastName, setLastName] = useState(card?.lastName || '');
  const [info, setInfo] = useState(card?.info || '');
  const [spouse, setSpouse] = useState(card?.spouse || '');
  const [children, setChildren] = useState(card?.children || '');
  const [parents, setParents] = useState(card?.parents || '');
  const [company, setCompany] = useState(card?.company || '');
  const [role, setRole] = useState(card?.role || '');
  const [linkedIn, setLinkedIn] = useState(card?.linkedIn || '');
  const [pronunciation, setPronunciation] = useState(card?.pronunciation || '');
  const [email, setEmail] = useState(card?.email || '');
  const [phone, setPhone] = useState(card?.phone || '');
  
  // Initialize relationships with proper structure and null checks
  const [relationships, setRelationships] = useState({
    spouse: card?.relationships?.spouse || null,
    parents: card?.relationships?.parents || [],
    children: card?.relationships?.children || [],
    siblings: card?.relationships?.siblings || []
  });

  // Initialize groups with proper null checks and normalization
  const [selectedGroups, setSelectedGroups] = useState(() => {
    if (!card) return [];
    
    // Handle both array and single group cases
    const groups = Array.isArray(card.groups) 
      ? card.groups 
      : (card.group ? [card.group] : []);
    
    // Normalize to strings
    return groups.map(g => {
      if (typeof g === 'string') return g;
      if (g && typeof g === 'object') return g.name || g.id || '';
      return '';
    }).filter(Boolean); // Remove any empty strings
  });

  // Add back currentGroup state
  const [currentGroup, setCurrentGroup] = useState('');

  // Add debug logging for initial state
  useEffect(() => {
    console.log('EditCardForm - Initial state:', {
      firstName,
      lastName,
      info,
      company,
      role,
      linkedIn,
      email,
      phone,
      selectedGroups,
      relationships,
      currentGroup
    });
  }, []);

  // Image handling
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(card.imageUrl);

  // Group suggestions
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const groupInputRef = useRef(null);

  // Add isOwner check
  const isOwner = card.userId === currentUserId;

  // Add these state variables inside EditCardForm
  const [showPhotoAlbum, setShowPhotoAlbum] = useState(false);
  const [albumPhotos, setAlbumPhotos] = useState([]);

  // Add error state
  const [error, setError] = useState(null);

  // Add this useEffect to log cards when they change
  useEffect(() => {
    console.log('Cards updated in EditCardForm:', cards);
  }, [cards]);

  // Handle click outside group suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupInputRef.current && !groupInputRef.current.contains(event.target)) {
        setShowGroupSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter groups based on input (always use group names)
  useEffect(() => {
    const filtered = (existingGroups || [])
      .map(g => (typeof g === 'string' ? g : g.name))
      .filter(g => 
        g.toLowerCase().includes(currentGroup.toLowerCase()) &&
        !selectedGroups.includes(g)
      );
    setFilteredGroups(filtered);
  }, [currentGroup, existingGroups, selectedGroups]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Add this function to load photos
  const loadAlbumPhotos = async () => {
    const q = query(collection(db, 'photoAlbum'), where('userId', '==', currentUserId));
    const snapshot = await getDocs(q);
    setAlbumPhotos(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // Add this function to handle photo selection
  const handlePhotoSelect = (photo) => {
    setPreview(photo.imageUrl);
    setShowPhotoAlbum(false);
    // We'll store the photo reference for the update
    setImage({ type: 'albumPhoto', photoId: photo.id, url: photo.imageUrl });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGroupSelect = (selectedGroup) => {
    if (!selectedGroups.includes(selectedGroup)) {
      setSelectedGroups([...selectedGroups, selectedGroup]);
    }
    setCurrentGroup('');
    setShowGroupSuggestions(false);
  };

  const handleRemoveGroup = (groupToRemove) => {
    setSelectedGroups(selectedGroups.filter(g => g !== groupToRemove));
  };

  const handleGroupKeyDown = (e) => {
    if (e.key === 'Enter' && currentGroup.trim()) {
      e.preventDefault();
      if (!selectedGroups.includes(currentGroup.trim())) {
        setSelectedGroups([...selectedGroups, currentGroup.trim()]);
      }
      setCurrentGroup('');
    }
  };

  const validateLinkedInUrl = (url) => {
    if (!url) return true; // Allow empty URL
    return url.startsWith('https://www.linkedin.com/') || 
           url.startsWith('http://www.linkedin.com/') ||
           url.startsWith('www.linkedin.com/') ||
           url.startsWith('linkedin.com/');
  };

  const [modelLoaded, setModelLoaded] = useState(false);

  // Add model loading effect
  useEffect(() => {
    const loadModels = async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/models')
        ]);
        setModelLoaded(true);
        console.log('Face-api models loaded successfully');
      } catch (error) {
        console.error('Error loading face-api models:', error);
      }
    };
    loadModels();
  }, []);

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 400; // Reduced from 800
          const MAX_HEIGHT = 400; // Reduced from 800
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with reduced quality
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg', 0.5); // Reduced quality to 50%
        };
      };
    });
  };

  const extractFaceDescriptor = async (imageUrl) => {
    if (!modelLoaded) {
      console.log('Face-api models not yet loaded');
      return null;
    }

    try {
      console.log('Starting face detection for:', imageUrl);
      const img = await faceapi.fetchImage(imageUrl);
      
      const detection = await faceapi
        .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (!detection) {
        console.log('No face detected in the image');
        return null;
      }
      
      console.log('Face detection successful');
      return Array.from(detection.descriptor);
    } catch (error) {
      console.error('Face detection error:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      const cardRef = doc(db, 'cards', card.id);
      
      // Get current data first
      const cardSnap = await getDoc(cardRef);
      if (!cardSnap.exists()) {
        throw new Error('Card not found');
      }
      const currentData = cardSnap.data();

      // Verify permissions
      const canEdit = currentData.canEdit || [];
      if (currentData.userId !== currentUserId && !canEdit.includes(currentUserId)) {
        throw new Error('You do not have permission to edit this card');
      }

      let newImageUrl = card.imageUrl;
      let faceDescriptor = currentData.faceDescriptor;

      // Handle image upload if there's a new image
      if (image) {
        if (image.type === 'albumPhoto') {
          newImageUrl = image.url;
          // Generate face descriptor for album photo
          faceDescriptor = await extractFaceDescriptor(image.url);
        } else {
          // Compress the image before uploading
          const compressedImage = await compressImage(image);
          const imageRef = ref(storage, `cards/${currentUserId}/${Date.now()}-${image.name}`);
          await uploadBytes(imageRef, compressedImage);
          newImageUrl = await getDownloadURL(imageRef);
          // Generate face descriptor for uploaded photo
          faceDescriptor = await extractFaceDescriptor(newImageUrl);
        }
      }

      // Build update object preserving existing fields
      const updateData = {
        info: info.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        pronunciation: pronunciation.trim(),
        company: company.trim(),
        role: role.trim(),
        email: email.trim(),
        phone: phone.trim(),
        linkedIn: linkedIn.trim(),
        groups: selectedGroups,
        group: selectedGroups?.[0] || currentData.group,
        imageUrl: newImageUrl,
        faceDescriptor, // Add the face descriptor
        faceApiDescriptor: faceDescriptor, // Add faceApiDescriptor as well
        relationships: {
          spouse: relationships.spouse ? {
            cardId: relationships.spouse.cardId,
            name: relationships.spouse.name,
            group: relationships.spouse.group
          } : null,
          parents: relationships.parents ? relationships.parents.map(p => ({
            cardId: p.cardId,
            name: p.name,
            group: p.group
          })) : [],
          children: relationships.children ? relationships.children.map(c => ({
            cardId: c.cardId,
            name: c.name,
            group: c.group
          })) : [],
          siblings: relationships.siblings ? relationships.siblings.map(s => ({
            cardId: s.cardId,
            name: s.name,
            group: s.group
          })) : []
        },
        updatedAt: serverTimestamp()
      };

      console.log('About to update with data:', updateData);
      await updateDoc(cardRef, updateData);
      
      toast.success('Card updated successfully');
      onClose();
    } catch (error) {
      console.error('Error updating card:', error);
      toast.error(error.message || 'Failed to update card');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isOwner) return;
    
    if (window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
      try {
        setLoading(true);
        const cardRef = doc(db, 'cards', card.id);
        
        // Soft delete by updating the deleted flag
        await updateDoc(cardRef, {
          deleted: true,
          deletedAt: new Date().toISOString()
        });
        
        onClose();
      } catch (error) {
        console.error('Error deleting card:', error);
        alert('Failed to delete card. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Defensive: ensure existingGroups are objects
  const safeExistingGroups = (existingGroups || []).map(group =>
    typeof group === 'string' ? { id: group, name: group } : group
  );

  let renderError = null;
  let formContent = null;
  try {
    formContent = (
      <>
        <div className="px-6 py-4 pt-24 pb-24">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Photo Upload */}
            <div>
              <div className="mb-2 flex justify-center">
                {card?.imageUrl ? (
                  <img 
                    src={card.imageUrl} 
                    alt="Current"
                    className="h-32 w-32 object-cover rounded-full"
                  />
                ) : (
                  <div className="h-32 w-32 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-gray-400">No image</span>
                  </div>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPhotoAlbum(true);
                    loadAlbumPhotos();
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Choose from Photo Gallery
                </button>
                <label className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer text-center">
                  Choose File
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Name Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full p-2 border rounded"
                  placeholder="Enter person's first name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter person's last name"
                />
              </div>
            </div>

            {/* Family Section */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-900 mb-3">Family Information</h3>
              <div className="space-y-4">
                <RelationshipField
                  label="Spouse"
                  value={relationships.spouse}
                  onChange={(relation) => {
                    setRelationships({
                      ...relationships,
                      spouse: relation
                    });
                  }}
                  cards={cards}
                  currentCardId={card.id}
                  placeholder="Search for spouse..."
                />

                <RelationshipField
                  label="Parents"
                  value={relationships.parents}
                  onChange={(relations) => setRelationships({
                    ...relationships,
                    parents: relations
                  })}
                  cards={cards}
                  currentCardId={card.id}
                  placeholder="Search for parents..."
                  multiple={true}
                />

                <RelationshipField
                  label="Children"
                  value={relationships.children}
                  onChange={(relations) => setRelationships({
                    ...relationships,
                    children: relations
                  })}
                  cards={cards}
                  currentCardId={card.id}
                  placeholder="Search for children..."
                  multiple={true}
                />

                <RelationshipField
                  label="Siblings"
                  value={relationships.siblings}
                  onChange={(relations) => setRelationships({
                    ...relationships,
                    siblings: relations
                  })}
                  cards={cards}
                  currentCardId={card.id}
                  placeholder="Search for siblings..."
                  multiple={true}
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Professional Information</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter role/title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  LinkedIn Profile
                </label>
                <input
                  type="text"
                  value={linkedIn}
                  onChange={(e) => setLinkedIn(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter LinkedIn URL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            {/* Groups Section */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Groups *
              </label>
              <div className="space-y-2">
                {selectedGroups.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedGroups.map(group => {
                      const groupName = typeof group === 'string' ? group : (group && (group.name || group.id)) || '';
                      return (
                        <span
                          key={groupName}
                          className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100"
                        >
                          {groupName}
                          <button
                            type="button"
                            onClick={() => handleRemoveGroup(groupName)}
                            className="ml-1 text-gray-500 hover:text-gray-700"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div ref={groupInputRef} className="relative">
                  <input
                    type="text"
                    value={currentGroup}
                    onChange={(e) => {
                      setCurrentGroup(e.target.value);
                      setShowGroupSuggestions(true);
                    }}
                    onKeyDown={handleGroupKeyDown}
                    onFocus={() => setShowGroupSuggestions(true)}
                    className="w-full p-2 border rounded"
                    placeholder="Add a group"
                  />
                  {showGroupSuggestions && filteredGroups.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                      {filteredGroups.map((suggestion) => {
                        const suggestionName = typeof suggestion === 'string' ? suggestion : (suggestion && (suggestion.name || suggestion.id)) || '';
                        return (
                          <div
                            key={suggestionName}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleGroupSelect(suggestionName)}
                          >
                            {suggestionName}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Notes</h3>
              <div>
                <textarea
                  value={info}
                  onChange={(e) => setInfo(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter any additional notes about this person"
                  rows="3"
                />
              </div>
            </div>

            {/* Pronunciation Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Pronunciation</h3>
              <div>
                <input
                  type="text"
                  value={pronunciation}
                  onChange={(e) => setPronunciation(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="How to pronounce their name (e.g., 'jah-nay')"
                />
              </div>
            </div>
          </form>
        </div>
      </>
    );
  } catch (err) {
    renderError = err;
  }

  return (
    <>
      <div className="sticky top-0 bg-white px-6 py-4 border-b rounded-t-lg z-10">
        <h2 className="text-2xl font-bold text-gray-900">Edit Card</h2>
      </div>
      <div className="overflow-y-auto flex-1 px-6 py-4" style={{ maxHeight: 'calc(90vh - 128px)' }}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Upload */}
          <div>
            <div className="mb-2 flex justify-center">
              {card?.imageUrl ? (
                <img 
                  src={card.imageUrl} 
                  alt="Current"
                  className="h-32 w-32 object-cover rounded-full"
                />
              ) : (
                <div className="h-32 w-32 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-gray-400">No image</span>
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setShowPhotoAlbum(true);
                  loadAlbumPhotos();
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Choose from Photo Gallery
              </button>
              <label className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer text-center">
                Choose File
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Name Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full p-2 border rounded"
                placeholder="Enter person's first name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter person's last name"
              />
            </div>
          </div>

          {/* Family Section */}
          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-900 mb-3">Family Information</h3>
            <div className="space-y-4">
              <RelationshipField
                label="Spouse"
                value={relationships.spouse}
                onChange={(relation) => {
                  setRelationships({
                    ...relationships,
                    spouse: relation
                  });
                }}
                cards={cards}
                currentCardId={card.id}
                placeholder="Search for spouse..."
              />

              <RelationshipField
                label="Parents"
                value={relationships.parents}
                onChange={(relations) => setRelationships({
                  ...relationships,
                  parents: relations
                })}
                cards={cards}
                currentCardId={card.id}
                placeholder="Search for parents..."
                multiple={true}
              />

              <RelationshipField
                label="Children"
                value={relationships.children}
                onChange={(relations) => setRelationships({
                  ...relationships,
                  children: relations
                })}
                cards={cards}
                currentCardId={card.id}
                placeholder="Search for children..."
                multiple={true}
              />

              <RelationshipField
                label="Siblings"
                value={relationships.siblings}
                onChange={(relations) => setRelationships({
                  ...relationships,
                  siblings: relations
                })}
                cards={cards}
                currentCardId={card.id}
                placeholder="Search for siblings..."
                multiple={true}
              />
            </div>
          </div>

          {/* Professional Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Professional Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter company name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <input
                type="text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter role/title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn Profile
              </label>
              <input
                type="text"
                value={linkedIn}
                onChange={(e) => setLinkedIn(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter LinkedIn URL"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          {/* Groups Section */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Groups *
            </label>
            <div className="space-y-2">
              {selectedGroups.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedGroups.map(group => {
                    const groupName = typeof group === 'string' ? group : (group && (group.name || group.id)) || '';
                    return (
                      <span
                        key={groupName}
                        className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100"
                      >
                        {groupName}
                        <button
                          type="button"
                          onClick={() => handleRemoveGroup(groupName)}
                          className="ml-1 text-gray-500 hover:text-gray-700"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div ref={groupInputRef} className="relative">
                <input
                  type="text"
                  value={currentGroup}
                  onChange={(e) => {
                    setCurrentGroup(e.target.value);
                    setShowGroupSuggestions(true);
                  }}
                  onKeyDown={handleGroupKeyDown}
                  onFocus={() => setShowGroupSuggestions(true)}
                  className="w-full p-2 border rounded"
                  placeholder="Add a group"
                />
                {showGroupSuggestions && filteredGroups.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredGroups.map((suggestion) => {
                      const suggestionName = typeof suggestion === 'string' ? suggestion : (suggestion && (suggestion.name || suggestion.id)) || '';
                      return (
                        <div
                          key={suggestionName}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleGroupSelect(suggestionName)}
                        >
                          {suggestionName}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Notes</h3>
            <div>
              <textarea
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter any additional notes about this person"
                rows="3"
              />
            </div>
          </div>

          {/* Pronunciation Section */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Pronunciation</h3>
            <div>
              <input
                type="text"
                value={pronunciation}
                onChange={(e) => setPronunciation(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="How to pronounce their name (e.g., 'jah-nay')"
              />
            </div>
          </div>
        </form>
      </div>
      <div className="sticky bottom-0 bg-white px-6 py-4 border-t rounded-b-lg flex justify-between items-center z-10">
        <div>
          {isOwner && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              Delete Card
            </button>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-[#04b53a] text-white rounded hover:bg-[#039431] disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
      {/* Photo Album Modal */}
      {showPhotoAlbum && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Photo Album</h3>
              <button
                onClick={() => setShowPhotoAlbum(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {albumPhotos.map((photo) => (
                <div
                  key={photo.id}
                  onClick={() => handlePhotoSelect(photo)}
                  className="cursor-pointer hover:opacity-75 transition-opacity"
                >
                  <img
                    src={photo.imageUrl}
                    alt="Album photo"
                    className="w-full h-32 object-cover rounded"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EditCardForm;